import { CommonModule, NgClass } from '@angular/common';
import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { DateRange } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatFormField, MatSelectModule, MatSuffix } from '@angular/material/select';
import { RestrictedElementDirective } from '@auth/directives';
import { createArbitraryValueChart, createPredefinedValuesChart } from '@charts';
import { APPLICATION_TYPE } from '@core';
import { ChildDevice, DeviceAdditionalAttribute, DeviceAttributeType, DeviceChart, DeviceData, DeviceService, MeasurementUnitShort } from '@devices';
import { AvsDateRangeIds, DatePickerComponent, DateRangeSelect$, getAvsDateRange, NoDataPlaceholderComponent } from '@ui';
import { sub } from 'date-fns';
import * as Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import merge from 'lodash.merge';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { forkJoin } from 'rxjs';

import { EnergyConsumptionWidgetComponent } from '../energy-consumption-widget/energy-consumption-widget.component';
import { Attribute, EditDataPointDialogComponent } from './edit-data-point-dialog.component';

@Component({
  imports: [
    CommonModule,
    MatIcon,
    MatProgressSpinner,
    MatSuffix,
    MatSelectModule,
    MatFormField,
    MatCardModule,
    NgxSkeletonLoaderModule,
    HighchartsChartComponent,
    RestrictedElementDirective,
    NgClass,
    NoDataPlaceholderComponent,
    EnergyConsumptionWidgetComponent,
    DatePickerComponent,
  ],
  selector: 'avs-device-stats-chart',
  styleUrls: ['./device-stats-chart.component.scss'],
  templateUrl: './device-stats-chart.component.html',
})
export class DeviceStatsChartComponent {
  device = input.required<DeviceData | ChildDevice>();
  locationId = input.required<string>();
  friendlyNameEditDisabled = input<boolean>(false);

  protected readonly isMobile = inject(APPLICATION_TYPE) === 'mobile';
  protected readonly attributes = computed(() => {
    const device = this.device();
    const realTimePoints = device?.additionalAttributes?.filter(
      attr => attr.attributeType === DeviceAttributeType.REALTIME,
    ) || [];

    return realTimePoints.map((point: DeviceAdditionalAttribute) => ({
      attributeType: point.attributeType,
      friendlyName: point.friendlyName,
      title: point.name,
      value: point.name,
    }));
  });

  protected deviceHasRealtimeAttributes = computed(() => this.attributes().length > 0);

  protected selectedChart = signal<string>('');

  protected selectedFriendlyName = computed(() => {
    const chartValue = this.selectedChart();

    return this.attributes().find(attr => attr.value === chartValue)?.friendlyName;
  });

  protected isLoading = signal(false);

  protected Highcharts = Highcharts;
  protected deviceCharts = signal<Record<string, DeviceChart | null>>({});
  protected chartData = signal<null | Highcharts.Options>(null);
  protected isEnergyConsumptionChart = false;

  protected minDate = sub(new Date(), { months: 1 });
  protected dateRange = signal<DateRange<Date> | null>(getAvsDateRange(AvsDateRangeIds.TODAY));
  protected selectedDateRangeId = signal<AvsDateRangeIds>(AvsDateRangeIds.TODAY);

  private deviceService = inject(DeviceService);
  private dialog = inject(MatDialog);
  private pausedState = signal<DeviceChart | null>(null);

  private defaultChartOptions: Highcharts.Options = {
    chart: {
      zooming: {
        resetButton: { theme: { style: { display: 'none' } } },
        type: 'x',
      },
    },
    time: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    xAxis: {
      events: {
        setExtremes: ({ max, min }) => {
          if (min && max) {
            const current = this.dateRange();

            if (current?.start?.getTime() !== min || current?.end?.getTime() !== max) {
              this.dateRange.set(new DateRange(new Date(min), new Date(max)));
            }
          }

          return false;
        },
      },
    },
  };

  private deviceId = computed(() => this.device().id);

  constructor() {
    effect(() => {
      const attrs = this.attributes();
      const currentSelected = this.selectedChart();

      untracked(() => {
        if (attrs.length > 0) {
          const isCurrentValid = attrs.some(a => a.value === currentSelected);

          if (!isCurrentValid) {
            const connectedIndex = attrs.findIndex(a => a.value === 'connected');
            const initialValue = attrs[connectedIndex >= 0 ? connectedIndex : 0].value;

            this.selectedChart.set(initialValue);
          }
        } else {
          this.selectedChart.set('');
        }

        this.deviceCharts.set({});
      });
    });

    effect(() => {
      const devId = this.deviceId();
      const range = this.dateRange();
      const chart = this.selectedChart();
      const locId = this.locationId();
      const hasRealtime = this.deviceHasRealtimeAttributes();

      if (devId && range && chart && locId && hasRealtime) {
        untracked(() => {
          this.loadCharts();
        });
      }
    });

    DateRangeSelect$.pipe(takeUntilDestroyed()).subscribe(range => {
      const connectedAttr = this.attributes().find(a => a.value === 'connected');

      if (connectedAttr) {
        this.selectedDateRangeId.set(AvsDateRangeIds.CUSTOM);
        this.deviceCharts.update(prev => ({ ...prev, [this.selectedChart()]: null }));
        this.dateRange.set(range);
        this.selectedChart.set(connectedAttr.value);
      }
    });
  }

  protected loadCharts() {
    if (this.isLoading()) {
      return;
    }

    const device = this.device();
    const chartId = this.selectedChart();
    const locId = this.locationId();
    const range = this.dateRange()!;

    if (!device || !chartId || !locId || !this.deviceHasRealtimeAttributes() || !this.dateRange()) {
      return;
    }

    const start = range.start!.toISOString();
    const end = range.end!.toISOString();

    this.isLoading.set(true);

    const timeseriesData$ = this.deviceService.getChartsData({
      data: { fromDateTimeUtc: start, point: chartId, toDateTimeUtc: end },
      deviceId: device.id,
    });

    const pausedData$ = this.deviceService.getChartsData({
      data: { fromDateTimeUtc: start, point: 'paused', toDateTimeUtc: end },
      deviceId: device.id,
    });

    forkJoin({
      chart: timeseriesData$,
      paused: pausedData$,
    }).subscribe({
      complete: () => {
        this.isLoading.set(false);
      },
      next: ({ chart, paused }) => {
        this.isLoading.set(false);
        this.pausedState.set(paused);

        this.deviceCharts.update(prev => ({ ...prev, [chartId]: chart }));
        this.generateChartData();
      },
    });
  }

  protected chartChange(value: string) {
    this.selectedChart.set(value);
    if (this.deviceCharts()[value]) {
      this.generateChartData();
    } else {
      this.loadCharts();
    }
  }

  protected generateChartData() {
    const currentChart = this.deviceCharts()[this.selectedChart()];
    const paused = this.pausedState();

    if (!currentChart) {
      return;
    }

    this.isEnergyConsumptionChart = currentChart.measurementUnit?.displayShort === 'kWh' as MeasurementUnitShort;
    this.chartData.set(null);
    setTimeout(() => {
      let chartData: Highcharts.Options;

      if (!currentChart.measurementUnit || !currentChart.measurementUnit.isStep) {
        chartData = createArbitraryValueChart({
          chart: currentChart,
          noTitle: true,
          paused,
          showDots: false,
        });
      }

      if (currentChart.measurementUnit?.isStep) {
        chartData = createPredefinedValuesChart({
          chart: currentChart,
          noTitle: true,
          paused,
          showDots: false,
        });
      }

      this.chartData.set(merge({}, this.defaultChartOptions, chartData!));
    }, 0);
  }

  protected refreshCharts() {
    if (this.isLoading()) {
      return;
    }

    const rangeId = this.selectedDateRangeId();

    if (rangeId !== AvsDateRangeIds.CUSTOM) {
      const updatedRange = getAvsDateRange(rangeId);

      this.dateRange.set(updatedRange);
    } else {
      this.loadCharts();
    }

    this.deviceCharts.update(prev => ({ ...prev, [this.selectedChart()]: null }));
  }

  protected openEditDataPointPopup(chart: Attribute) {
    const dialogRef = this.dialog.open(EditDataPointDialogComponent, {
      backdropClass: 'backdrop-modal-panel',
      data: {
        attribute: chart,
        device: this.device(),
      },
    });

    dialogRef.afterClosed().subscribe(() => this.loadCharts());
  }
}
